// Customer Support / Complaints — domain constants.

export const CATEGORIES = {
    DELIVERY: 'delivery',
    PRODUCT: 'product',
    REFUND: 'refund',
    REPLACEMENT: 'replacement',
    GENERAL: 'general',
};

export const STATUSES = [
    'open', 'pending', 'waiting_customer', 'doctor_review',
    'courier_escalation', 'refund_pending', 'replacement_pending',
    'resolved', 'closed',
];

export const STATUS_LABELS = {
    open: 'Open',
    pending: 'Pending',
    waiting_customer: 'Waiting on Customer',
    doctor_review: 'Doctor Review',
    courier_escalation: 'Courier Escalation',
    refund_pending: 'Refund Pending',
    replacement_pending: 'Replacement Pending',
    resolved: 'Resolved',
    closed: 'Closed',
};

export const DEPARTMENTS = ['support', 'doctor', 'courier', 'accounts', 'packing', 'manager'];

export const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

export const SOURCES = ['manual', 'excel_import', 'customer_portal', 'whatsapp', 'call', 'ai_chat'];

// Issue type -> category mapping. Drives the Create form and category inference.
export const ISSUE_TYPES = {
    // A. Delivery / Courier
    'Product Not Delivered': CATEGORIES.DELIVERY,
    'Delivery Delay': CATEGORIES.DELIVERY,
    'Delivered Status but Product Not Received': CATEGORIES.DELIVERY,
    'Undelivered': CATEGORIES.DELIVERY,
    'Pincode Issue': CATEGORIES.DELIVERY,
    'Mobile Number Issue': CATEGORIES.DELIVERY,
    'Denied Home Delivery': CATEGORIES.DELIVERY,
    'Order Not Taken / Not Shipped': CATEGORIES.DELIVERY,
    'Wrong Shipment': CATEGORIES.DELIVERY,
    'COD RTO Issue': CATEGORIES.DELIVERY,
    'Long Pending Delivery': CATEGORIES.DELIVERY,
    // A2. Ekart courier-specific (all route to Courier Team / DELIVERY category)
    'Ekart Delivery Delay': CATEGORIES.DELIVERY,
    'Ekart Shipment Not Picked': CATEGORIES.DELIVERY,
    'Ekart Out For Delivery Too Long': CATEGORIES.DELIVERY,
    'Ekart NDR': CATEGORIES.DELIVERY,
    'Ekart Customer Refused': CATEGORIES.DELIVERY,
    'Ekart Delivered But Not Received': CATEGORIES.DELIVERY,
    'Ekart Fake Delivery': CATEGORIES.DELIVERY,
    'Ekart Lost Shipment': CATEGORIES.DELIVERY,
    'Ekart Damaged Shipment': CATEGORIES.DELIVERY,
    'Ekart Wrong Delivery': CATEGORIES.DELIVERY,
    'Ekart Returned To Origin': CATEGORIES.DELIVERY,
    'Ekart Pincode Not Serviceable': CATEGORIES.DELIVERY,
    'Ekart Hub Delay': CATEGORIES.DELIVERY,
    'Ekart Label Issue': CATEGORIES.DELIVERY,
    'Ekart Address Verification Required': CATEGORIES.DELIVERY,
    // B. Product / Doctor
    'Pimple': CATEGORIES.PRODUCT,
    'Dryness': CATEGORIES.PRODUCT,
    'Skin Irritation': CATEGORIES.PRODUCT,
    'Burning Sensation': CATEGORIES.PRODUCT,
    'Dandruff': CATEGORIES.PRODUCT,
    'No Result': CATEGORIES.PRODUCT,
    'Quality Concern': CATEGORIES.PRODUCT,
    'Product Reaction': CATEGORIES.PRODUCT,
    'Doctor Call Required': CATEGORIES.PRODUCT,
    // C. Refund / Replacement
    'Refund Issued': CATEGORIES.REFUND,
    'Refund Pending': CATEGORIES.REFUND,
    'Replacement Required': CATEGORIES.REPLACEMENT,
    'Reship Required': CATEGORIES.REPLACEMENT,
    'Return Pickup Required': CATEGORIES.REPLACEMENT,
    'Paid Extra': CATEGORIES.REFUND,
    'Wrong Amount Paid': CATEGORIES.REFUND,
    // D. General
    'Customer Callback': CATEGORIES.GENERAL,
    'Payment Confirmation': CATEGORIES.GENERAL,
    'Order Edit Required': CATEGORIES.GENERAL,
    'Other Complaint': CATEGORIES.GENERAL,
};

// Issue types that route to the doctor workflow.
export const DOCTOR_ISSUE_TYPES = [
    'Pimple', 'Dryness', 'Skin Irritation', 'Burning Sensation',
    'Dandruff', 'No Result', 'Quality Concern', 'Product Reaction', 'Doctor Call Required',
];

// SLA hours by priority (section 18).
export const SLA_HOURS = { urgent: 4, high: 24, medium: 48, low: 72 };

// Customer SMS / WhatsApp templates (section 11). {ticketNumber} is interpolated.
export const NOTIFY_TEMPLATES = {
    registered: 'Your complaint {ticketNumber} has been registered. Our team will update you soon.',
    doctor_review: 'Your complaint has been forwarded to our doctor review team.',
    waiting_customer: 'Please share required details/photos to continue your complaint review.',
    courier_escalation: 'Your delivery issue has been escalated to our courier team.',
    refund_pending: 'Your refund for complaint {ticketNumber} is being processed. We will update you soon.',
    replacement_pending: 'A replacement for your complaint {ticketNumber} is being arranged.',
    resolved: 'Your complaint has been resolved. Thank you for your patience.',
    closed: 'Your complaint has been closed.',
};

export const fillTemplate = (key, ticketNumber) =>
    (NOTIFY_TEMPLATES[key] || '').replace('{ticketNumber}', ticketNumber || '');

// Map a complaint template key → the central notification event key.
export const TEMPLATE_EVENT_MAP = {
    registered: 'complaint_registered',
    doctor_review: 'complaint_doctor_review',
    waiting_customer: 'complaint_waiting_customer',
    courier_escalation: 'complaint_courier_escalation',
    refund_pending: 'complaint_refund_pending',
    replacement_pending: 'complaint_replacement_pending',
    resolved: 'complaint_resolved',
    closed: 'complaint_closed',
};

export const categoryForIssue = (issueType) => ISSUE_TYPES[issueType] || CATEGORIES.GENERAL;
